import { useEffect, useRef } from 'react';
import { Spinner, Stack, Typography } from '@prenuvo/halo-foundation';

import { FCT } from '@/i18n';
import { useIntersectionObserver } from '@/shared/hook';
import { Loading } from '@/shared/ui';

import { IGNORED_FIELDS } from './activity-log.constant';
import { ActivityLogContent } from './activity-log-content';
import { ActivityLogHeader } from './activity-log-header';
import { useGetActivityLogs } from './use-get-activity-logs.hook';

export function ActivityLogsState() {
  const fetchRef = useRef<HTMLDivElement>(null);
  const isIntersecting = useIntersectionObserver(fetchRef, { threshold: 1 });

  const { fetchNextPage, hasNextPage, isFetchingNextPage, items, status } = useGetActivityLogs();

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isIntersecting]);

  if (status === 'pending') {
    return <Loading />;
  }

  if (!items || items?.length === 0) {
    return (
      <Stack className="rounded-xl border p-20" justification="center">
        <Typography>{FCT.ACTIVITY_LOGS.EMPTY}</Typography>
      </Stack>
    );
  }

  return (
    <>
      {items.map((activityLog) => {
        const allFieldsMatch =
          activityLog?.objectDiff &&
          Object.keys(activityLog.objectDiff).every((key) => IGNORED_FIELDS.includes(key));

        if (allFieldsMatch) {
          return null;
        }

        return (
          <div key={activityLog.uuid} className="pb-5">
            <ActivityLogHeader actor={activityLog.actor} timestamp={activityLog.timestamp} />
            <ActivityLogContent activityLog={activityLog} />
          </div>
        );
      })}

      <div ref={fetchRef} style={{ height: 1 }} />

      {isFetchingNextPage && (
        <Stack className="p-20" justification="center">
          <Spinner size="sm" variant="white" />
        </Stack>
      )}

      {!hasNextPage && (
        <Stack className="border-t p-20" justification="center">
          <Typography>{FCT.ACTIVITY_LOGS.END}</Typography>
        </Stack>
      )}
    </>
  );
}
